import React from "react";
import { toast } from "react-toastify";

export const DAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

export const MEAL_TYPES = [
  { key: "breakfast", label: "Breakfast" },
  { key: "lunch", label: "Lunch" },
  { key: "dinner", label: "Dinner" },
  { key: "snacks", label: "Snacks" },
  { key: "munching", label: "Munching" },
];

export const emptyDayCell = () => ({
  text: "",
  imageFile: null,
  imagePreview: null,
  imageUrl: null,
});

export const createMealRow = () =>
  DAYS.reduce((acc, day) => {
    acc[day] = emptyDayCell();
    return acc;
  }, {});

export const createWeek = (weekNum) => ({
  week: String(weekNum),
  breakfast: createMealRow(),
  lunch: createMealRow(),
  dinner: createMealRow(),
  snacks: createMealRow(),
  munching: createMealRow(),
});

export const createEmptyWeeks = () => [1].map(createWeek);

export const takeSingleWeek = (weeks) => {
  if (!weeks?.length) return [];
  const weekOne = weeks.find((w) => String(w.week) === "1");
  return [weekOne || weeks[0]];
};

export const normalizeCell = (value) => {
  if (value && typeof value === "object") {
    return {
      text: value.text ?? value.spec ?? "",
      imageFile: value.imageFile ?? null,
      imagePreview: value.imagePreview ?? null,
      imageUrl: value.imageUrl ?? value.image ?? null,
    };
  }
  return {
    text: value ?? "",
    imageFile: null,
    imagePreview: null,
    imageUrl: null,
  };
};

export const cellHasMealContent = (value) => {
  const cell = normalizeCell(value);
  const text = (cell.text || "").trim();
  if (text && text !== "N/A") return true;
  return !!(cell.imageUrl || cell.imagePreview);
};

export const filterWeeksWithContent = (weeks) =>
  (weeks || []).filter((week) =>
    MEAL_TYPES.some(({ key }) =>
      DAYS.some((day) => cellHasMealContent(week[key]?.[day]))
    )
  );

export const sanitizeCellForSave = (value) => {
  const c = normalizeCell(value);
  return {
    text: c.text,
    imageUrl: c.imageUrl,
    imageFile: null,
    imagePreview: null,
  };
};

export const cloneWeeksSnapshot = (weeks) =>
  (weeks || []).map((week) => {
    const row = { week: week.week };
    MEAL_TYPES.forEach(({ key }) => {
      row[key] = {};
      DAYS.forEach((day) => {
        row[key][day] = sanitizeCellForSave(week[key]?.[day]);
      });
    });
    return row;
  });

export const cellIsDirty = (current, saved) => {
  const cur = normalizeCell(current);
  const sav = normalizeCell(saved);
  if ((cur.text || "").trim() !== (sav.text || "").trim()) return true;
  if (cur.imageFile) return true;
  const curVisual = cur.imagePreview || cur.imageUrl || null;
  const savVisual = sav.imageUrl || null;
  return curVisual !== savVisual;
};

export const collectDirtyCells = (weeks, savedWeeks) => {
  const dirty = [];
  weeks.forEach((week, weekIndex) => {
    MEAL_TYPES.forEach(({ key }) => {
      DAYS.forEach((day) => {
        if (cellIsDirty(week[key]?.[day], savedWeeks[weekIndex]?.[key]?.[day])) {
          dirty.push({
            weekIndex,
            mealType: key,
            day,
            cell: normalizeCell(week[key]?.[day]),
          });
        }
      });
    });
  });
  return dirty;
};

export const buildPlanPayload = (weeks) =>
  weeks.map((w) => {
    const row = { week: w.week };
    MEAL_TYPES.forEach(({ key }) => {
      row[key] = {};
      DAYS.forEach((day) => {
        const cell = normalizeCell(w[key]?.[day]);
        row[key][day] = cell.text;
      });
    });
    return row;
  });

export const mapWeeksFromApiResponse = (apiData) => {
  const planes = apiData?.[0]?.plane ?? [];
  const mapped = planes.map((weekRow) => {
    const weekData = { week: String(weekRow.week) };
    const specs = weekRow.specs ?? {};
    MEAL_TYPES.forEach(({ key }) => {
      weekData[key] = {};
      DAYS.forEach((day) => {
        weekData[key][day] = specs[key]?.[day] ?? "";
      });
    });
    return weekData;
  });
  return takeSingleWeek(mapped);
};

export const appendMealImagesToFormData = (formData, weeks) => {
  weeks.forEach((w) => {
    MEAL_TYPES.forEach(({ key }) => {
      DAYS.forEach((day) => {
        const cell = normalizeCell(w[key]?.[day]);
        if (cell.imageFile) {
          formData.append(
            `image_${w.week}_${key}_${day}`,
            cell.imageFile,
            cell.imageFile.name
          );
        }
      });
    });
  });
};

const ACCEPTED_TYPES = /^image\/(jpeg|jpg|png|gif|webp)$/i;

export default function MealPlanCell({
  value,
  onChange,
  readOnly = false,
  disableImageUpload = false,
  syncTextOnChange = false,
}) {
  const cell = normalizeCell(value);
  const preview = cell.imagePreview || cell.imageUrl;

  const handleText = (e) => {
    const next = { ...cell, text: e.target.value };
    if (syncTextOnChange) {
      onChange(next);
    }
  };

  const handleTextBlur = (e) => {
    if (!syncTextOnChange) {
      onChange({ ...cell, text: e.target.value });
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.test(file.type)) {
      toast.error("Please upload JPG, PNG, GIF, or WEBP only");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be 5MB or smaller");
      e.target.value = "";
      return;
    }

    if (cell.imagePreview) {
      URL.revokeObjectURL(cell.imagePreview);
    }

    onChange({
      ...cell,
      imageFile: file,
      imagePreview: URL.createObjectURL(file),
    });
    e.target.value = "";
  };

  const removeImage = () => {
    if (cell.imagePreview) {
      URL.revokeObjectURL(cell.imagePreview);
    }
    onChange({
      ...cell,
      imageFile: null,
      imagePreview: null,
      imageUrl: null,
    });
  };

  return (
    <div className="meal-plan-cell">
      <textarea
        className="form-control meal-field"
        rows={3}
        value={cell.text}
        readOnly={readOnly}
        onChange={syncTextOnChange ? handleText : undefined}
        onBlur={handleTextBlur}
        placeholder="Meal notes..."
      />
      {!readOnly && !disableImageUpload && (
        <label className="btn btn-sm btn-outline-secondary w-100 mt-1 mb-0 meal-plan-upload-btn">
          <i className="bi bi-image me-1" />
          Add photo
          <input
            type="file"
            className="d-none"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleFile}
          />
        </label>
      )}
      {preview ? (
        <div className="meal-plan-cell-preview mt-1">
          <a href={preview} target="_blank" rel="noreferrer" title="Open image">
            <img src={preview} alt="Meal" />
          </a>
          {!readOnly && !disableImageUpload && (
            <button
              type="button"
              className="btn btn-sm btn-danger meal-plan-remove-img"
              onClick={removeImage}
              title="Remove image"
            >
              <i className="bi bi-x" />
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}
